export const ACTION_TYPE_ANIMATE = ["Animate", "Animate_EN", "Animate_FR", "AnimateSKU"];
export const GRAPHICS_WEBM_SUPPORT = checkWebMSupport();
export const GRAPHICS_WEBP_SUPPORT = checkWebPSupport();
export let GRAPHICS_ANIMATED_WEBP_SUPPORT = null;

export default class Graphics {
  static async init() {
    return await checkAnimatedWebPSupport().then(result => {
      const { supported, mime } = result;
      console.log(`This device %c%s%c ${mime}.`, `color: ${supported ? "green" : "red"}; font-weight: bold;`, supported ? "supports" : "does not support", "");
      GRAPHICS_ANIMATED_WEBP_SUPPORT = supported;
    });
  }
}

/**
 * Rewrites Azure Blob (or configured) media URLs to use the local dev proxy when in development.
 * This avoids CORS by making requests same-origin to the Vue dev server, which proxies to the target.
 *
 * Rules:
 * - Only active when process.env.NODE_ENV === 'development'.
 * - If the URL host matches one of the known media hosts, return `${prefix}${pathname}${search}`.
 * - Otherwise, return the original URL unchanged.
 * - Data URLs, blob URLs, and relative URLs are returned unchanged.
 *
 * You can customize via env:
 * - VUE_APP_MEDIA_PROXY_PREFIX (default: '/media')
 * - VUE_APP_MEDIA_HOSTS (comma-separated list). Default includes 'ctcdigitalmedia.blob.core.windows.net'.
 */
export function devProxyUrl(url) {
  try {
    if (!url || typeof url !== 'string') return url;
    // Only in development mode
    if (typeof process === 'undefined' || process.env.NODE_ENV !== 'development') return url;

    // Ignore data: and blob: schemes
    const lower = url.toLowerCase();
    if (lower.startsWith('data:') || lower.startsWith('blob:')) return url;

    const u = new URL(url, typeof window !== 'undefined' ? window.location.href : undefined);
    const hostsEnv = (typeof process !== 'undefined' && process.env.VUE_APP_MEDIA_HOSTS) ? process.env.VUE_APP_MEDIA_HOSTS : '';
    const knownHosts = hostsEnv.split(',').map(h => h.trim()).filter(Boolean);
    if (knownHosts.length === 0) {
      knownHosts.push('ctcdigitalmedia.blob.core.windows.net');
    }
    const matches = knownHosts.includes(u.host);
    if (!matches) return url;

    const prefix = (typeof process !== 'undefined' && process.env.VUE_APP_MEDIA_PROXY_PREFIX) || '/media';
    return `${prefix}${u.pathname}${u.search}`;
  } catch (e) {
    // If parsing fails, return original URL
    return url;
  }
}

/**
 * Extracts the file extension from a given URL or file path.
 *
 * @param {string} url - The URL or file path from which to extract the file extension.
 * @return {string} The file extension in lowercase if found, or an empty string if no extension exists.
 */
export function getURLExt(url){
  let uString = '';

  try{
    const u = new URL(url);
    uString = u.pathname;
  } catch(e) {
    uString = decodeURIComponent(url);
  }

  return uString.split('.').pop().toLowerCase();
}

/**
 * Retrieves the MIME type associated with a file extension extracted from a given URL.
 *
 * @param {string} url - The URL or file path from which the file extension will be determined.
 * @return {string|null} The corresponding MIME type for the file extension if found, or null if the extension is not recognized.
 */
export function getMimeTypeFromExtension(url) {
  const extension = getURLExt(url);// uString.split('.').pop().toLowerCase();
  const mimeTypes = {
    'webm': 'video/webm',
    'webp': 'image/webp',
    'mp4': 'video/mp4',
    'jpg': 'image/jpeg',
    'jpeg': 'image/jpeg',
    'png': 'image/png',
    'gif': 'image/gif',
    'svg': 'image/svg+xml'
  };
  return mimeTypes[extension] || null;
}


function checkWebMSupport() {
  const video = document.createElement("video");
  const canPlay = video.canPlayType("video/webm; codecs=\"vp8, vorbis\"");
  //I don't know why I wasted time on this... At least it pops out!.
  let c = { maybe: "yellow", probably: "green" }[canPlay] ?? "red";
  console.log("This device can %c%s%c play webm videos.", `color: ${c}; font-weight: bold;`, canPlay ?? "not", "");
  return canPlay !== "";
}

/**
 * Checks if the current device supports the WebP image format.
 *
 * @param {string} [type=""] An optional type indicator, e.g., "lossy", "lossless", "alpha", or "animation" to test specific WebP features. *note* This does not work for animation use the checkAnimatedWebPSupport() function instead which returns a promise instead of a boolean
 * @return {boolean} Returns true if the specified WebP format is supported, otherwise false.
 */
function checkWebPSupport(type = "") {
  const du = `image/webp${type ? `;${type}=1` : ""}`;
  const supported = document.createElement("canvas")
    .toDataURL(du)
    .indexOf(`data:${du}`) === 0;
  console.log(`This device %c%s%c ${du}.`, `color: ${supported ? "green" : "red"}; font-weight: bold;`, supported ? "supports" : "does not support", "");
  return supported;
}

/**
 * Checks if the browser supports animated WebP images.
 * It first checks if a global `GRAPHICS_WEBP_SUPPORT` flag is available, then validates support through an actual animated WebP image sample.
 *
 * @return {Promise<Object>} A promise that resolves to an object containing:
 * - `supported` (boolean): Indicates whether animated WebP is supported.
 * - `mime` (string): The MIME type for animated WebP ("image/webp;animation=1").
 */
function checkAnimatedWebPSupport() {
  return new Promise((resolve) => {
    // This is a valid animated WebP image base64
    const du = 'image/webp;animation=1';
    const animatedWebP = "data:image/webp;base64,UklGRlIAAABXRUJQVlA4WAoAAAASAAAAAAAAAAAAQU5JTQYAAAD/////AABBTk1GJgAAAAAAAAAAAAAAAAAAAGQAAABWUDhMDQAAAC8AAAAQBxAREYiI/gcA";

    const img = new Image();
    img.onload = () => resolve({ supported: true, mime: du });
    img.onerror = () => resolve({ supported: false, mime: du });
    img.src = animatedWebP;

  });
}
